import type { FormEvent, RefObject } from 'react';
import { Button, Textarea } from '@/shared/ui';
import { ErrorBanner } from '@/shared/ui/ErrorBanner';

export const FEEDBACK_MAX_LENGTH = 4000;

export type FeedbackFormProps = {
  /** The library card's "Report an issue" scrolls this into view. */
  formRef: RefObject<HTMLFormElement | null>;
  value: string;
  onChange: (value: string) => void;
  submitting: boolean;
  /** Latches after a successful send so the button can say so. */
  sent: boolean;
  error: string | null;
  onDismissError: () => void;
  onSubmit: (event: FormEvent) => void;
};

/**
 * Free-text feedback on a finished video.
 *
 * The length cap is enforced on the way in as well as declared: `maxLength`
 * stops typing but not a paste in every browser, and the counter has to agree
 * with what would actually be sent.
 */
export function FeedbackForm({
  formRef,
  value,
  onChange,
  submitting,
  sent,
  error,
  onDismissError,
  onSubmit,
}: FeedbackFormProps) {
  return (
    <form ref={formRef} className="content-page__feedback" onSubmit={onSubmit}>
      <h3 className="content-page__feedback-title">Feedback</h3>
      <p className="content-page__feedback-note">
        Tell us what’s working or what’s not about this video — we read every note.
      </p>
      <Textarea
        value={value}
        onChange={(event) => onChange(event.target.value.slice(0, FEEDBACK_MAX_LENGTH))}
        rows={4}
        maxLength={FEEDBACK_MAX_LENGTH}
        placeholder="What do you like or dislike about this video?"
        aria-label="Video feedback"
        hint={`${value.length}/${FEEDBACK_MAX_LENGTH}`}
      />
      {error ? <ErrorBanner message={error} onDismiss={onDismissError} /> : null}
      <Button type="submit" variant="secondary" disabled={submitting || !value.trim()}>
        {submitting ? 'Sending…' : sent ? 'Sent — thank you!' : 'Send feedback'}
      </Button>
    </form>
  );
}
